import { forbidExtraProps, nonNegativeInteger } from 'airbnb-prop-types';
import { DateTime } from 'luxon'; // import luxon DateTime
import PropTypes from 'prop-types';
import raf from 'raf';
import React from 'react';

import { CalendarDayPhrases } from '../defaultPhrases';
import ModifiersShape from '../shapes/ModifiersShape';
import getCalendarDaySettings from '../utils/getCalendarDaySettings';
import getPhrasePropTypes from '../utils/getPhrasePropTypes';

import { DAY_SIZE } from '../constants';
import mergeClassNames from '../utils/mergeClassNames';
import mergeStyles from '../utils/mergeStyles';

const propTypes = forbidExtraProps({

  day: PropTypes.instanceOf(DateTime), // changed to luxon DateTime
  daySize: nonNegativeInteger,
  isOutsideDay: PropTypes.bool,
  modifiers: ModifiersShape,
  isFocused: PropTypes.bool,
  tabIndex: PropTypes.oneOf([0, -1]),
  onDayClick: PropTypes.func,
  onDayMouseEnter: PropTypes.func,
  onDayMouseLeave: PropTypes.func,
  renderDayContents: PropTypes.func,
  ariaLabelFormat: PropTypes.string,

  // internationalization
  phrases: PropTypes.shape(getPhrasePropTypes(CalendarDayPhrases)),
});

const defaultProps = {
  day: DateTime.now(), // changed to luxon DateTime
  daySize: DAY_SIZE,
  isOutsideDay: false,
  modifiers: new Set(),
  isFocused: false,
  tabIndex: -1,
  onDayClick() {},
  onDayMouseEnter() {},
  onDayMouseLeave() {},
  renderDayContents: null,
  ariaLabelFormat: 'dddd, LL',

  // internationalization
  phrases: CalendarDayPhrases,
};

class CalendarDay extends React.PureComponent {
  constructor(...args) {
    super(...args);

    this.setButtonRef = this.setButtonRef.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { isFocused, tabIndex } = this.props;
    if (tabIndex === 0) {
      if (isFocused || tabIndex !== prevProps.tabIndex) {
        raf(() => {
          if (this.buttonRef) {
            this.buttonRef.focus();
          }
        });
      }
    }
  }

  onDayClick(day, e) {
    const { onDayClick } = this.props;
    onDayClick(day, e);
  }

  onDayMouseEnter(day, e) {
    const { onDayMouseEnter } = this.props;
    onDayMouseEnter(day, e);
  }

  onDayMouseLeave(day, e) {
    const { onDayMouseLeave } = this.props;
    onDayMouseLeave(day, e);
  }

  onKeyDown(day, e) {
    const { onDayClick } = this.props;

    const { key } = e;
    if (key === 'Enter' || key === ' ') {
      onDayClick(day, e);
    }
  }

  setButtonRef(ref) {
    this.buttonRef = ref;
  }

  render() {
    const {
      day,
      ariaLabelFormat,
      daySize,
      isOutsideDay,
      modifiers,
      renderDayContents,
      tabIndex,

      phrases,
    } = this.props;

    // eslint-disable-next-line jsx-a11y/control-has-associated-label
    if (!day) return <td />;

    const {
      daySizeStyles,
      useDefaultCursor,
      selected,
      hoveredSpan,
      isOutsideRange,
      ariaLabel,
    } = getCalendarDaySettings(day, ariaLabelFormat, daySize, modifiers, phrases);

    return (
      <td
        style={mergeStyles(
          daySizeStyles,
        )}
        className={mergeClassNames(
          'CalendarDay',
          useDefaultCursor && 'CalendarDay__defaultCursor',
          'CalendarDay__default',
          isOutsideDay && 'CalendarDay__outside',
          modifiers.has('today') && 'CalendarDay__today',
          modifiers.has('first-day-of-week') && 'CalendarDay__firstDayOfWeek',
          modifiers.has('last-day-of-week') && 'CalendarDay__lastDayOfWeek',
          modifiers.has('hovered-offset') && 'CalendarDay__hovered_offset',
          modifiers.has('hovered-start-first-possible-end') && 'CalendarDay__hovered_start_first_possible_end',
          modifiers.has('hovered-start-blocked-minimum-nights') && 'CalendarDay__hovered_start_blocked_min_nights',
          modifiers.has('highlighted-calendar') && 'CalendarDay__highlighted_calendar',
          modifiers.has('blocked-minimum-nights') && 'CalendarDay__blocked_minimum_nights',
          modifiers.has('blocked-calendar') && 'CalendarDay__blocked_calendar',
          hoveredSpan && 'CalendarDay__hovered_span',
          modifiers.has('after-hovered-start') && 'CalendarDay__after_hovered_start',
          modifiers.has('selected-span') && 'CalendarDay__selected_span',
          modifiers.has('selected-start') && 'CalendarDay__selected_start',
          modifiers.has('selected-end') && 'CalendarDay__selected_end',
          selected && !modifiers.has('selected-span') && 'CalendarDay__selected',
          modifiers.has('before-hovered-end') && 'CalendarDay__before_hovered_end',
          modifiers.has('no-selected-start-before-selected-end') && 'CalendarDay__no_selected_start_before_selected_end',
          modifiers.has('selected-start-in-hovered-span') && 'CalendarDay__selected_start_in_hovered_span',
          modifiers.has('selected-end-in-hovered-span') && 'CalendarDay__selected_end_in_hovered_span',
          modifiers.has('selected-start-no-selected-end') && 'CalendarDay__selected_start_no_selected_end',
          modifiers.has('selected-end-no-selected-start') && 'CalendarDay__selected_end_no_selected_start',
          isOutsideRange && 'CalendarDay__blocked_out_of_range',
        )}
        role="button"
        ref={this.setButtonRef}
        aria-disabled={modifiers.has('blocked')}
        {...(modifiers.has('today') ? { 'aria-current': 'date' } : {})}
        aria-label={ariaLabel}
        onMouseEnter={(e) => { this.onDayMouseEnter(day, e); }}
        onMouseLeave={(e) => { this.onDayMouseLeave(day, e); }}
        onMouseUp={(e) => { e.currentTarget.blur(); }}
        onClick={(e) => { this.onDayClick(day, e); }}
        onKeyDown={(e) => { this.onKeyDown(day, e); }}
        tabIndex={tabIndex}
      >

        {renderDayContents ? renderDayContents(day, modifiers) : day.toFormat('d')}
      </td>
    );
  }
}

CalendarDay.propTypes = propTypes;
CalendarDay.defaultProps = defaultProps;

export { CalendarDay as PureCalendarDay };
export default CalendarDay;
